import { useState, useEffect } from 'react';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { AlertCircle, CheckCircle, Loader2, RefreshCw, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { projectId, publicAnonKey } from '../utils/supabase/info';

const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-d4ddf86c`;

interface ConnectionStatusProps {
  onStatusChange?: (isConnected: boolean) => void;
}

export function ConnectionStatus({ onStatusChange }: ConnectionStatusProps) {
  const [status, setStatus] = useState<'checking' | 'connected' | 'disconnected'>('checking');
  const [retryCount, setRetryCount] = useState(0);
  const [lastError, setLastError] = useState<string>('');
  const [isDeploymentIssue, setIsDeploymentIssue] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const checkConnection = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      const response = await fetch(`${API_BASE}/health`, {
        headers: {
          'Authorization': `Bearer ${publicAnonKey}`
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        console.log('Backend connected:', data);
        setStatus('connected');
        onStatusChange?.(true);
        setRetryCount(0);
        setLastError('');
        setIsDeploymentIssue(false);
      } else {
        const errorMsg = `Server returned status ${response.status}`;
        setLastError(errorMsg);
        setStatus('disconnected');
        onStatusChange?.(false);
        setIsDeploymentIssue(response.status === 404);
      }
    } catch (error) {
      // Only log non-abort errors (AbortError is expected on timeout)
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          setLastError('Connection timeout - backend not responding');
          setIsDeploymentIssue(true);
        } else {
          console.error('Backend connection check failed:', error);
          setLastError(error.message || 'Unknown error');
          // "Failed to fetch" usually means CORS or function not deployed
          if (error.message.includes('Failed to fetch')) {
            setIsDeploymentIssue(true);
          }
        }
      } else {
        setLastError('Unknown error occurred');
      }
      setStatus('disconnected');
      onStatusChange?.(false);
    }
  };

  useEffect(() => {
    checkConnection();
    // Check connection every 30 seconds
    const interval = setInterval(checkConnection, 30000);
    return () => clearInterval(interval);
  }, [retryCount]);

  const handleRetry = () => {
    setIsRetrying(true);
    setStatus('checking');
    setRetryCount(prev => prev + 1);
    checkConnection().finally(() => setIsRetrying(false));
  };

  if (status === 'connected') {
    return null; // Don't show anything when connected
  }

  return (
    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-2xl px-4">
      <Alert 
        variant={status === 'checking' ? 'default' : 'destructive'}
        className="border-2 shadow-lg"
      >
        <div className="flex items-start gap-3">
          {status === 'checking' && (
            <Loader2 className="h-5 w-5 animate-spin shrink-0 mt-0.5" />
          )}
          {status === 'disconnected' && (
            <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
          )}
          <div className="flex-1 min-w-0">
            <AlertTitle>
              {status === 'checking' && 'Connecting to Backend...'}
              {status === 'disconnected' && 'Backend Connection Error'}
            </AlertTitle>
            <AlertDescription className="mt-2">
              {status === 'checking' && (
                <p className="text-sm">Establishing connection to the server...</p>
              )}
              {status === 'disconnected' && (
                <>
                  <p className="text-sm mb-2">
                    Unable to connect to the backend server. This is likely due to a Supabase deployment issue.
                  </p>
                  {lastError && (
                    <p className="text-xs text-muted-foreground mb-3 font-mono bg-black/5 p-2 rounded">
                      Error: {lastError}
                    </p>
                  )}
                  <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 mb-3">
                    <p className="text-xs font-medium mb-2">To fix this issue:</p>
                    <ol className="text-xs space-y-1 list-decimal list-inside">
                      <li>Check the Supabase connection in Figma Make</li>
                      <li>Ensure the edge function is properly deployed</li>
                      <li>Verify your Supabase project credentials</li>
                      <li>Try reconnecting your Supabase project</li>
                    </ol>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      onClick={handleRetry} 
                      size="sm" 
                      variant="outline"
                      className="shrink-0"
                      loading={isRetrying}
                      loadingText="Retrying..."
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Retry Connection
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      Attempt {retryCount > 0 ? retryCount : 1}
                    </span>
                  </div>
                </>
              )}
            </AlertDescription>
          </div>
        </div>
      </Alert>
    </div>
  );
}